import type { AxiosError } from 'axios'
import { defineStore, storeToRefs } from 'pinia'
import type { Card } from '@academica-box/components/Cards/UiProductCard/types'
import type { MappedLanding, PayRequestData } from '@academica-box/api/courses'
import type { SubscribtionOpenData } from '@academica-box/api/subscribes'
import { useSubscribesStore } from '@academica-box/composables/store/subscribes'
import type { Rating } from '@academica-box/api/reviews'
import type { VendorReview } from '@academica-box/types'

export const useLandingStore = defineStore('landing', {
  state: () => ({
    landing: undefined,
    courses: undefined,
    requestId: undefined,
    redirectUrl: null,
    reviews: undefined,
    rating: undefined,
  } as {
    landing?: MappedLanding;
    courses?: Card[];
    requestId?: string;
    redirectUrl: string | null; // Бэк может прислать null
    reviews?: VendorReview[];
    rating?: Rating;
  }),

  actions: {
    async getLanding(slug: string) {
      const { $api, $sentry } = useNuxtApp()
      const subscribesStore = useSubscribesStore()
      const { isSubscriptionAvailable } = storeToRefs(subscribesStore)

      try {
        const result = await $api.courses.getLanding(slug, {
          isSubscriptionAvailable: isSubscriptionAvailable.value,
        })

        this.landing = result

        return this.landing
      } catch (error) {
        $sentry.captureException(error)
      }
    },

    async getReviews(nomenclatureId?: number) {
      const { $api, $sentry } = useNuxtApp()

      try {
        const response = await $api.reviews.getReviews({
          entityType: 'academica_landings',
          nomenclatureId,
          limit: 12,
        })

        this.reviews = response.data

        return response
      } catch (error) {
        $sentry.captureException(error)
      }
    },

    async getRating(id?: number) {
      const { $api, $sentry } = useNuxtApp()

      try {
        const response = await $api.reviews.getRating([id || -1])

        this.rating = response[String(id)]

        return response
      } catch (error) {
        $sentry.captureException(error)
      }
    },

    async getCourses(slug: string, params?: { isPromo?: boolean }) {
      const subscribesStore = useSubscribesStore()
      const { isSubscriptionAvailable } = storeToRefs(subscribesStore)
      const { $api, $sentry } = useNuxtApp()

      try {
        const result = await $api.courses.getCoursesBySlug(slug, {
          isPromo: params?.isPromo,
          isSubscriptionAvailable: isSubscriptionAvailable.value,
          isAcademgen: true,
        })

        this.courses = result
      } catch (error) {
        $sentry.captureException(error)
      }
    },

    async openCourse(data: PayRequestData) {
      const { $api, $sentry, $userSession } = useNuxtApp()
      const sessionId = $userSession.session?.id ?? ''

      try {
        this.requestId = data.request_id

        const recaptchaToken = await $api.recaptcha.generateToken()
        const result = await $api.courses.openCourse({
          ...data,
          recaptcha_token: recaptchaToken || '',
          session_id: sessionId,
        })

        this.redirectUrl = result.redirect_url
      } catch (error) {
        $sentry.captureException(error)
        throw new Error((error as AxiosError).message)
      }
    },

    async openSubscription(data: SubscribtionOpenData) {
      const { $api, $sentry, $userSession } = useNuxtApp()
      const sessionId = $userSession.session?.id ?? ''

      try {
        this.requestId = data.request_id
        const recaptchaToken = await $api.recaptcha.generateToken()

        await $api.subscribes.openSubscription({
          ...data,
          recaptcha_token: recaptchaToken || '',
          session_id: sessionId,
        })
      } catch (error) {
        $sentry.captureException(error)
        throw new Error((error as AxiosError).message)
      }
    },
  },
})
